/**
 * AIInsightsPanel Component
 * 
 * AI-themed panel displaying data quality scores, confidence levels,
 * database statistics, and neural network animation
 */

import React, { useState, useEffect } from 'react';
import { Card, ProgressBar, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBrain,
  faLightbulb,
  faDatabase,
  faClock,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faRobot,
  faCog,
  faChartPie,
  faRefresh
} from '@fortawesome/free-solid-svg-icons';

// Import types
import type { 
  AIInsightsPanelProps,
  ConfidenceLevel 
} from '../../../../types/marketIntelligence.types';

// Confidence level configuration
const CONFIDENCE_CONFIG: Record<ConfidenceLevel, {
  color: string;
  icon: any;
  description: string;
  bgGradient: string;
}> = {
  'High': {
    color: '#10b981',
    icon: faCheckCircle,
    description: 'Excellent data quality with high prediction accuracy',
    bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)'
  },
  'Medium': {
    color: '#f59e0b',
    icon: faExclamationTriangle,
    description: 'Good data quality with moderate prediction accuracy',
    bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)'
  },
  'Low': {
    color: '#ef4444',
    icon: faInfoCircle,
    description: 'Limited data quality affecting prediction accuracy',
    bgGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)'
  }
};

// Neural network node component
const NeuralNode: React.FC<{ 
  x: number; 
  y: number; 
  size: number; 
  active: boolean;
  delay: number;
}> = ({ x, y, size, active, delay }) => (
  <circle
    cx={x}
    cy={y}
    r={size}
    fill={active ? '#00d4ff' : '#6b7280'}
    opacity={active ? 1 : 0.3}
    style={{
      transition: 'all 0.3s ease',
      animationDelay: `${delay}s`
    }}
  >
    <animate
      attributeName="opacity"
      values="0.3;1;0.3"
      dur="2s"
      repeatCount="indefinite"
      begin={`${delay}s`}
    />
  </circle>
);

// Neural network animation component
const NeuralNetworkAnimation: React.FC = () => {
  const [activeNodes, setActiveNodes] = useState<number[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveNodes(prev => {
        const newActive = [];
        for (let i = 0; i < 12; i++) {
          if (Math.random() > 0.6) {
            newActive.push(i);
          }
        }
        return newActive;
      });
    }, 1500);

    return () => clearInterval(interval);
  }, []);

  const nodes = [
    // Input layer
    { x: 20, y: 25, size: 4 },
    { x: 20, y: 45, size: 4 },
    { x: 20, y: 65, size: 4 },
    { x: 20, y: 85, size: 4 },
    
    // Hidden layer 1
    { x: 60, y: 30, size: 3 },
    { x: 60, y: 50, size: 3 },
    { x: 60, y: 70, size: 3 },
    
    // Hidden layer 2
    { x: 100, y: 35, size: 3 },
    { x: 100, y: 55, size: 3 },
    { x: 100, y: 75, size: 3 },
    
    // Output layer
    { x: 140, y: 45, size: 4 },
    { x: 140, y: 65, size: 4 }
  ];

  const connections = [
    // Input to hidden1
    [0, 4], [0, 5], [0, 6],
    [1, 4], [1, 5], [1, 6],
    [2, 4], [2, 5], [2, 6],
    [3, 4], [3, 5], [3, 6],
    
    // Hidden1 to hidden2
    [4, 7], [4, 8], [4, 9],
    [5, 7], [5, 8], [5, 9],
    [6, 7], [6, 8], [6, 9],
    
    // Hidden2 to output
    [7, 10], [7, 11],
    [8, 10], [8, 11],
    [9, 10], [9, 11]
  ];

  return (
    <div className="neural-network-container mb-3">
      <svg width="160" height="110" viewBox="0 0 160 110">
        {/* Connections */}
        {connections.map(([from, to], index) => (
          <line
            key={index}
            x1={nodes[from].x}
            y1={nodes[from].y}
            x2={nodes[to].x}
            y2={nodes[to].y}
            stroke="#00d4ff"
            strokeWidth="1"
            opacity={activeNodes.includes(from) && activeNodes.includes(to) ? 0.6 : 0.1}
            style={{ transition: 'opacity 0.3s ease' }}
          />
        ))}
        
        {/* Nodes */}
        {nodes.map((node, index) => (
          <NeuralNode
            key={index}
            x={node.x}
            y={node.y}
            size={node.size}
            active={activeNodes.includes(index)}
            delay={index * 0.1}
          />
        ))}
      </svg>
    </div>
  );
};

// Data quality gauge component
const DataQualityGauge: React.FC<{ score: number }> = ({ score }) => {
  const getColor = (score: number): string => {
    if (score >= 90) return '#10b981';
    if (score >= 70) return '#f59e0b';
    if (score >= 50) return '#ef4444';
    return '#6b7280';
  };

  const color = getColor(score);
  const circumference = 2 * Math.PI * 45;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="d-flex align-items-center justify-content-center mb-3">
      <div className="position-relative">
        <svg width="120" height="120" className="transform-rotate-90">
          {/* Background circle */}
          <circle
            cx="60"
            cy="60"
            r="45"
            fill="none"
            stroke="rgba(107, 114, 128, 0.2)"
            strokeWidth="8"
          />
          {/* Progress circle */}
          <circle
            cx="60"
            cy="60"
            r="45"
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            style={{
              transition: 'stroke-dashoffset 1s ease-in-out',
              filter: `drop-shadow(0 0 6px ${color}50)`
            }}
          />
        </svg>
        
        {/* Score text */}
        <div 
          className="position-absolute top-50 start-50 translate-middle text-center"
          style={{ transform: 'rotate(-90deg)' }}
        >
          <div 
            style={{ 
              fontSize: '1.5rem', 
              fontWeight: '700', 
              color,
              lineHeight: 1
            }}
          >
            {score}%
          </div>
          <div 
            style={{ 
              fontSize: '0.7rem', 
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            Quality
          </div>
        </div>
      </div>
    </div>
  );
};

// Loading skeleton
const LoadingSkeleton: React.FC = () => (
  <div>
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={index} className="mb-3">
        <div 
          style={{
            height: '60px',
            background: 'linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 212, 255, 0.3) 50%, rgba(0, 212, 255, 0.1) 100%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s infinite',
            borderRadius: '8px'
          }}
        />
      </div>
    ))}
  </div>
);

const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({
  dataQuality = 0,
  confidence = 'Low',
  lastUpdated,
  recordCount = 0,
  loading = false
}) => {
  const [processingStatus, setProcessingStatus] = useState<'idle' | 'processing' | 'complete'>('idle');
  const [refreshing, setRefreshing] = useState(false);

  const confidenceConfig = CONFIDENCE_CONFIG[confidence];
  
  // Simulate processing status
  useEffect(() => {
    if (dataQuality > 0) {
      setProcessingStatus('complete');
    }
  }, [dataQuality]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setProcessingStatus('processing');
    
    // Simulate refresh delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setRefreshing(false);
    setProcessingStatus('complete');
  };

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  if (loading) {
    return (
      <Card 
        className="h-100 border-0"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          borderRadius: '16px'
        }}
      >
        <Card.Body className="p-3">
          <LoadingSkeleton />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card 
      className="h-100 border-0"
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
        backdropFilter: 'blur(15px)',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        borderRadius: '16px'
      }}
    >
      <Card.Header 
        className="border-0 bg-transparent d-flex align-items-center justify-content-between"
        style={{ borderRadius: '16px 16px 0 0' }}
      >
        <div className="d-flex align-items-center">
          <FontAwesomeIcon 
            icon={faBrain} 
            style={{ 
              color: '#00d4ff', 
              fontSize: '18px',
              marginRight: '12px',
              filter: 'drop-shadow(0 0 4px rgba(0, 212, 255, 0.3))'
            }} 
          />
          <h6 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>
            AI Insights
          </h6>
        </div>

        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            borderColor: 'rgba(107, 114, 128, 0.3)',
            color: '#6b7280',
            fontSize: '0.75rem'
          }}
        >
          <FontAwesomeIcon 
            icon={faRefresh} 
            className={refreshing ? 'fa-spin' : ''}
          />
        </Button>
      </Card.Header>

      <Card.Body className="p-3">
        {/* Neural Network Animation */}
        <div className="text-center mb-4">
          <NeuralNetworkAnimation />
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            AI Neural Network Processing
          </div>
        </div>

        {/* Data Quality Score */}
        {dataQuality > 0 && (
          <div className="mb-4">
            <DataQualityGauge score={dataQuality} />
            <div className="text-center">
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>
                Data Quality Score
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                Based on completeness, accuracy & consistency
              </div>
            </div>
          </div>
        )}

        {/* Confidence Level */}
        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Confidence Level
            </span>
            <FontAwesomeIcon 
              icon={confidenceConfig.icon}
              style={{ color: confidenceConfig.color, fontSize: '14px' }}
            />
          </div>
          
          <Badge 
            style={{
              background: confidenceConfig.bgGradient,
              color: confidenceConfig.color,
              border: `1px solid ${confidenceConfig.color}30`,
              borderRadius: '20px',
              padding: '8px 16px',
              fontSize: '0.8rem',
              fontWeight: '600',
              width: '100%',
              textAlign: 'center'
            }}
          >
            {confidence} Confidence
          </Badge>
          
          <div 
            style={{ 
              fontSize: '0.75rem', 
              color: '#6b7280', 
              marginTop: '8px',
              lineHeight: 1.4
            }}
          >
            {confidenceConfig.description}
          </div>
        </div>

        {/* Database Statistics */}
        <div className="mb-4">
          <div className="d-flex align-items-center mb-3">
            <FontAwesomeIcon 
              icon={faDatabase}
              style={{ color: '#8b5cf6', fontSize: '16px', marginRight: '8px' }}
            />
            <span style={{ fontSize: '0.875rem', color: '#1f2937', fontWeight: '600' }}>
              Database Statistics
            </span>
          </div>
          
          <div className="row g-2">
            <div className="col-6">
              <div 
                className="text-center p-2"
                style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(139, 92, 246, 0.2)'
                }}
              >
                <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#8b5cf6' }}>
                  {recordCount.toLocaleString()}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                  Total Records
                </div>
              </div>
            </div>
            
            <div className="col-6">
              <div 
                className="text-center p-2"
                style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(16, 185, 129, 0.2)'
                }}
              >
                <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#10b981' }}>
                  241+
                </div>
                <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                  ZIP Codes
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Processing Status */}
        <div className="mb-4">
          <div className="d-flex align-items-center mb-2">
            <FontAwesomeIcon 
              icon={faCog}
              style={{ 
                color: processingStatus === 'processing' ? '#00d4ff' : '#10b981',
                fontSize: '14px',
                marginRight: '8px',
                animation: processingStatus === 'processing' ? 'spin 2s linear infinite' : 'none'
              }}
            />
            <span style={{ fontSize: '0.875rem', color: '#1f2937', fontWeight: '600' }}>
              Processing Status
            </span>
          </div>
          
          <div 
            className="d-flex align-items-center p-2"
            style={{
              background: processingStatus === 'complete' 
                ? 'rgba(16, 185, 129, 0.1)' 
                : 'rgba(245, 158, 11, 0.1)',
              borderRadius: '8px',
              border: `1px solid ${processingStatus === 'complete' ? '#10b98130' : '#f59e0b30'}`
            }}
          >
            <div 
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: processingStatus === 'complete' ? '#10b981' : '#f59e0b',
                marginRight: '8px',
                animation: processingStatus === 'processing' ? 'pulse 2s infinite' : 'none'
              }}
            />
            <span style={{ 
              fontSize: '0.8rem', 
              color: processingStatus === 'complete' ? '#10b981' : '#f59e0b',
              fontWeight: '500'
            }}>
              {processingStatus === 'complete' ? 'Data Processing Complete' : 
               processingStatus === 'processing' ? 'Processing Updates...' : 'Ready for Processing'}
            </span>
          </div>
        </div>

        {/* Last Updated */}
        {lastUpdated && (
          <div className="mb-0">
            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon 
                icon={faClock}
                style={{ color: '#6b7280', fontSize: '14px', marginRight: '8px' }}
              />
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Last Updated
              </span>
            </div>
            
            <div style={{ fontSize: '0.8rem', color: '#1f2937', fontWeight: '600' }}>
              {formatRelativeTime(lastUpdated)}
            </div>
            <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
              {new Date(lastUpdated).toLocaleString()}
            </div>
          </div>
        )}
      </Card.Body>

      {/* Custom CSS */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .transform-rotate-90 {
          transform: rotate(-90deg);
        }
      `}</style>
    </Card>
  );
};

export default AIInsightsPanel;